import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  APPLICATION_PROGRAM_LABELS,
  applicationProgramOptions,
  applicationSentLine,
  sendClientApplication,
  type ApplicationProgram,
} from "@/lib/accountActions";
import type { BondProgramType } from "@/lib/brokerBonds";
import { isEmailShaped } from "@/lib/email";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { ActionFormDialog } from "@/components/ActionFormDialog";
import { SelectField } from "@/components/SelectField";
import { TextAreaField } from "@/components/TextAreaField";
import { TextField } from "@/components/TextField";

// BRK-S412 — "Send application to client" from the Surety account's Actions
// menu: PMS emails the insured the Signal Express or Signal Advance application
// (PMS-S840, D4; pinned at BRK-S511). The recipient starts as the insured's email of
// record and can be changed; Signal Advance is offered to an insured on Express
// or on no program. "Application sent to {email}" is said only from the 2xx.

const FIELDS = ["program", "email", "note"] as const;
const NOTE_MAX = 2000;

export function SendApplicationDialog({
  open,
  onClose,
  accountId,
  defaultEmail,
  heldProgram,
}: {
  open: boolean;
  onClose: () => void;
  accountId: string;
  /** The insured's email of record — the recipient it starts with. */
  defaultEmail: string;
  /** The program the insured holds, or null for none. */
  heldProgram: BondProgramType | null;
}) {
  const { authedRequest } = useAuth();
  const programs = applicationProgramOptions(heldProgram);
  // An Express insured is most likely being asked to move up.
  const firstChoice: ApplicationProgram =
    heldProgram === "express" ? "advance" : "express";
  const [program, setProgram] = useState<ApplicationProgram>(firstChoice);
  const [email, setEmail] = useState(defaultEmail);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | null>(null);

  // Fresh each time it opens.
  useEffect(() => {
    if (!open) return;
    setProgram(firstChoice);
    setEmail(defaultEmail);
    setNote("");
    setError(null);
    setInvalid({});
    setSuccess(null);
  }, [open, defaultEmail, firstChoice]);

  async function send() {
    const to = email.trim();
    const missing: Record<string, string> = {};
    if (to === "") missing.email = "Enter the client’s email address.";
    else if (!isEmailShaped(to)) missing.email = "Enter a valid email address.";
    setInvalid(missing);
    setError(null);
    if (Object.keys(missing).length > 0) return;
    setBusy(true);
    try {
      const receipt = await sendClientApplication(authedRequest, accountId, {
        program,
        email: to,
        note,
      });
      setSuccess(applicationSentLine(receipt, to));
    } catch (err) {
      const anchored = anchorServerError(err, [...FIELDS]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionFormDialog
      open={open}
      onClose={onClose}
      title="Send application to client"
      lead="We’ll email the client a secure link to complete and sign the application."
      submitLabel="Send application"
      busy={busy}
      error={error}
      success={success}
      onSubmit={() => void send()}
    >
      <TextField
        label="Send to"
        type="email"
        autoComplete="off"
        value={email}
        required
        onChange={(e) => setEmail(e.target.value)}
        error={invalid.email}
      />
      <SelectField
        label="Application"
        value={program}
        options={programs.map((p) => ({
          value: p,
          label: APPLICATION_PROGRAM_LABELS[p],
        }))}
        onChange={(e) => {
          const next = programs.find((p) => p === e.target.value);
          if (next) setProgram(next);
        }}
        error={invalid.program}
      />
      <TextAreaField
        label="Note to client"
        optional
        maxLength={NOTE_MAX}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        error={invalid.note}
      />
    </ActionFormDialog>
  );
}
